/**
 * Character Calculations
 * Utilities for calculating character combat stats (evasion, armor score, thresholds, proficiency)
 */

import { isDomainTouchedActive } from './domainCountUtils';
import { getPassiveBonus } from '../config/mechanicsConfig';
import { hasLoadoutCard, getNormalizedLoadout } from './cardFeatureDetection';
import { hasSubclass } from './featureDetection';

/**
 * Domain-Touched card configurations for passive bonus calculation
 * Maps card keys to their passive bonuses
 */
const DOMAIN_TOUCHED_CONFIGS = {
  'arcana-touched': {
    domain: 'ARCANA',
    bonuses: { spellcast: 1 },
  },
  'blade-touched': {
    domain: 'BLADE',
    bonuses: { attackRoll: 2, severeThreshold: 4 },
  },
  'bone-touched': {
    domain: 'BONE',
    bonuses: { agility: 1 },
  },
  'codex-touched': {
    domain: 'CODEX',
    // Codex passive is conditional (mark stress for +Prof to Spellcast) - handled separately
    bonuses: {},
  },
  'grace-touched': {
    domain: 'GRACE',
    // Grace passive is conditional (armor slot = stress) - handled separately
    bonuses: {},
  },
  'midnight-touched': {
    domain: 'MIDNIGHT',
    // Midnight passive is conditional (at 0 Hope) - handled separately
    bonuses: {},
  },
  'sage-touched': {
    domain: 'SAGE',
    // Sage passive is conditional (natural environment) - handled separately
    bonuses: {},
  },
  'splendor-touched': {
    domain: 'SPLENDOR',
    bonuses: { severeThreshold: 3 },
  },
  'valor-touched': {
    domain: 'VALOR',
    bonuses: { armorScore: 1 },
  },
};

/**
 * Get all Domain-Touched passive bonuses for a character
 * Checks which Domain-Touched cards are in loadout and active (4+ domain cards)
 *
 * @param {object} character - Character data with loadout array
 * @param {object} allData - Game data with domainCards
 * @returns {object} Bonuses object: { armorScore, severeThreshold, attackRoll, agility, spellcast, ... }
 */
export const getDomainTouchedBonuses = (character, allData) => {
  const bonuses = {
    armorScore: 0,
    severeThreshold: 0,
    attackRoll: 0,
    agility: 0,
    spellcast: 0,
    // Conditional bonuses tracked as flags
    codexStressForProficiency: false,
    graceArmorSlotAsStress: false,
    midnightZeroHopeFear: false,
    sageNaturalEnvironmentSpellcast: 0,
  };

  if (!character?.loadout || !allData?.domainCards) {
    return bonuses;
  }

  // Check each Domain-Touched card
  Object.entries(DOMAIN_TOUCHED_CONFIGS).forEach(([cardKey, config]) => {
    // Check if card is in loadout
    if (!hasLoadoutCard(character, cardKey)) return;

    // Check if Domain-Touched condition is met (4+ cards from domain)
    if (!isDomainTouchedActive(character, allData, config.domain)) return;

    // Apply bonuses
    Object.entries(config.bonuses).forEach(([stat, value]) => {
      bonuses[stat] += value;
    });

    // Set conditional bonus flags
    if (cardKey === 'codex-touched') {
      bonuses.codexStressForProficiency = true;
    }
    if (cardKey === 'grace-touched') {
      bonuses.graceArmorSlotAsStress = true;
    }
    if (cardKey === 'midnight-touched') {
      bonuses.midnightZeroHopeFear = true;
    }
    if (cardKey === 'sage-touched') {
      bonuses.sageNaturalEnvironmentSpellcast = 2;
    }
  });

  return bonuses;
};

/**
 * Calculate evasion with armor modifiers
 * @param {object} character - Character data
 * @param {object} armorInfo - Armor data
 * @returns {number} Calculated evasion value
 */
export const calculateEvasion = (character, armorInfo) => {
  let baseEvasion = character.evasion || 10;

  // Apply armor modifiers
  const armorFeature = armorInfo?.feature || '';
  if (armorFeature.includes('Heavy: −1 to Evasion')) {
    baseEvasion -= 1;
  } else if (armorFeature.includes('Very Heavy: −2 to Evasion')) {
    baseEvasion -= 2;
  } else if (armorFeature.includes('Flexible: +1 to Evasion')) {
    baseEvasion += 1;
  } else if (armorFeature.includes('Barrier: +2 to Armor Score; −1 to Evasion')) {
    baseEvasion -= 1;
  }

  // Config-driven evasion bonuses (ancestry, domain cards, subclasses)
  // Includes: Simiah Nimble, Untouchable, Shadowhunter, Nightwalker Fleeting Shadow
  baseEvasion += getPassiveBonus(character, 'evasion');

  // Warden of the Elements (Druid) - Air Dominion: +1 Evasion while channeling Air
  // Note: Complex condition (requires channeling state), kept inline
  const masteryFeatures = character?.subclassFeatures?.masteries || [];
  if (hasSubclass(character, 'warden-of-the-elements') &&
      character?.isChanneling && character?.channelingElement === 'air' &&
      masteryFeatures.includes('Elemental Dominion')) {
    baseEvasion += 1;
  }

  // Beastform evasion bonus (Druid transformation)
  if (character?.activeBeastform?.evasionBonus) {
    baseEvasion += character.activeBeastform.evasionBonus;
  }

  // Conjure Shield (School of War Wizard) - +Proficiency Evasion when Hope ≥ 2
  // SRD: "While you have at least 2 Hope, you add your Proficiency to your Evasion."
  if (hasSubclass(character, 'school-of-war') && (character?.hope || 0) >= 2) {
    const prof = character?.proficiency || 2;
    baseEvasion += prof;
  }

  // Elusive Predator (Wayfinder Ranger) - +2 Evasion vs Focus target
  // SRD: "When your Focus makes an attack against you, you gain a +2 bonus to your Evasion"
  // Note: Applied as passive bonus; player tracks when defending against Focus target
  if (hasSubclass(character, 'wayfinder') && character?.elusivePredatorActive) {
    baseEvasion += 2;
  }

  // Transcendence (Elemental Origin Sorcerer) - +2 Evasion if selected
  // SRD: "Choose two benefits: +2 bonus to your Evasion" (among other options)
  if (character?.transcendenceBenefits?.includes('evasion')) {
    baseEvasion += 2;
  }

  return baseEvasion;
};

/**
 * Get beastform trait bonus for a specific trait
 * @param {object} character - Character data
 * @param {string} trait - Trait name (e.g., 'Agility', 'Strength')
 * @returns {number} Bonus from beastform (0 if not transformed)
 */
export const getBeastformTraitBonus = (character, trait) => {
  if (!character?.activeBeastform) return 0;

  let bonus = 0;
  const traitKey = trait.toLowerCase();

  // Form trait bonus
  if (character.activeBeastform.traitBonuses?.[traitKey]) {
    bonus += character.activeBeastform.traitBonuses[traitKey];
  }

  // Evolution bonus (+1 to chosen trait)
  if (character.activeBeastform.evolutionBonus?.trait?.toLowerCase() === traitKey) {
    bonus += character.activeBeastform.evolutionBonus.amount || 1;
  }

  return bonus;
};

/**
 * Check if character is currently in beastform
 * @param {object} character - Character data
 * @returns {boolean} True if transformed
 */
export const isInBeastform = (character) => {
  return !!character?.activeBeastform;
};

/**
 * Get Transcendence trait bonus for a specific trait
 * @param {object} character - Character data
 * @param {string} trait - Trait name (e.g., 'Agility', 'Strength')
 * @returns {number} Bonus from Transcendence (0 if not active or different trait)
 */
export const getTranscendenceTraitBonus = (character, trait) => {
  if (!character?.transcendenceBenefits?.includes('trait')) return 0;
  if (!character?.transcendenceTrait) return 0;

  // Case-insensitive comparison
  const normalizedTrait = trait?.toLowerCase?.();
  const selectedTrait = character.transcendenceTrait?.toLowerCase?.();

  return normalizedTrait === selectedTrait ? 1 : 0;
};

/**
 * Get total trait bonus including all sources (beastform, transcendence, etc.)
 * @param {object} character - Character data
 * @param {string} trait - Trait name
 * @returns {number} Total bonus from all sources
 */
export const getTotalTraitBonus = (character, trait) => {
  let bonus = 0;

  // Beastform trait bonus
  bonus += getBeastformTraitBonus(character, trait);

  // Transcendence trait bonus
  bonus += getTranscendenceTraitBonus(character, trait);

  return bonus;
};

/**
 * Calculate total armor score including shields and armor features
 * @param {object} armorInfo - Armor data
 * @param {object} secondaryWeaponInfo - Secondary weapon data (for shield bonuses)
 * @param {object} character - Character data (for domain card bonuses)
 * @returns {number} Total armor score
 */
export const calculateArmorScore = (armorInfo, secondaryWeaponInfo, character = {}) => {
  let totalScore = armorInfo?.baseScore || 0;

  // Add armor feature bonuses
  const armorFeature = armorInfo?.feature || '';
  if (armorFeature.includes('Barrier: +2 to Armor Score; −1 to Evasion')) {
    totalScore += 2;
  }

  // Add shield bonuses from secondary weapon
  if (secondaryWeaponInfo) {
    const shieldFeature = secondaryWeaponInfo.feature || '';

    if (shieldFeature.includes('Protective: +1 to Armor Score')) {
      totalScore += 1;
    } else if (shieldFeature.includes('Protective: +2 to Armor Score')) {
      totalScore += 2;
    } else if (shieldFeature.includes('Double Duty: +1 to Armor Score')) {
      totalScore += 1;
    } else if (shieldFeature.includes('Barrier: +2 to Armor Score')) {
      totalScore += 2;
    }
  }

  // Domain card bonuses (follow same pattern as ancestry bonuses)
  // Armorer (VALOR): +1 Armor Score while wearing armor
  // SRD: "While you're wearing armor, gain a +1 bonus to your Armor Score."
  if (armorInfo && hasLoadoutCard(character, 'armorer')) {
    totalScore += 1;
  }

  return totalScore;
};

/**
 * Get character tier based on level
 * Tier 1: Levels 1-2, Tier 2: Levels 3-4, Tier 3: Levels 5-6, Tier 4: Levels 7-10
 * @param {number} level - Character level
 * @returns {number} Character tier (1-4)
 */
export const getCharacterTier = (level) => {
  if (level <= 2) return 1;
  if (level <= 4) return 2;
  if (level <= 6) return 3;
  return 4;
};

/**
 * Bare Bones damage thresholds by tier (from SRD)
 * Note: Bare Bones has NO Minor threshold - only Major and Severe
 */
const BARE_BONES_THRESHOLDS = {
  1: { major: 9, severe: 19 },
  2: { major: 11, severe: 24 },
  3: { major: 13, severe: 31 },
  4: { major: 15, severe: 38 },
};

/**
 * Calculate unarmored AC for Bare Bones domain card
 * @param {object} character - Character data with traits and loadout
 * @returns {number} Unarmored AC value (0 if not applicable)
 */
export const calculateUnarmoredAC = (character) => {
  // Bare Bones (VALOR): Base AC 3+STR when unarmored
  // SRD: "When you choose not to equip armor, you have a base Armor Score of 3 + your Strength"
  if (hasLoadoutCard(character, 'bare-bones') && !character?.armor) {
    return 3 + (character.traits?.Strength || 0);
  }

  return 0;
};

/**
 * Calculate Bare Bones damage thresholds when unarmored
 * SRD specifies tier-based thresholds with Major and Severe ONLY (no Minor threshold)
 * @param {object} character - Character data with loadout and level
 * @returns {object|null} Damage thresholds { major, severe } or null if not applicable
 */
export const calculateBareBonesThresholds = (character) => {
  // Bare Bones (VALOR): Special tier-based thresholds when unarmored
  // SRD: "...and use the following as your base damage thresholds:
  //   Tier 1: 9/19, Tier 2: 11/24, Tier 3: 13/31, Tier 4: 15/38"
  if (hasLoadoutCard(character, 'bare-bones') && !character?.armor) {
    const level = character?.level || 1;
    const tier = getCharacterTier(level);
    return BARE_BONES_THRESHOLDS[tier];
  }

  return null;
};

/**
 * Calculate damage thresholds: armor base thresholds + character level + subclass bonuses
 * @param {object} armorInfo - Armor data with base thresholds
 * @param {number} level - Character level
 * @param {object} character - Character data (for subclass bonuses)
 * @returns {object} Object with minor, major, and severe threshold values
 */
export const calculateDamageThresholds = (armorInfo, level, character = {}) => {
  let minor, major, severe;

  if (armorInfo) {
    minor = (armorInfo.baseThresholds.minor || 3) + level;
    major = (armorInfo.baseThresholds.major || 7) + level;
    severe = (armorInfo.baseThresholds.severe || 13) + level;
  } else {
    // Unarmored characters: Minor = level, Major = 2 × level
    minor = level;
    major = 2 * level;
    severe = 4 * level;
  }

  // Config-driven threshold bonuses (ancestry, subclass, domain cards)
  // Includes: Stalwart tiers (Unwavering/Unrelenting/Undaunted), Winged Sentinel Ascendant,
  // Galapa Shell, Rise Up - all with proper tier replacement handling
  const allThresholdsBonus = getPassiveBonus(character, 'all-thresholds');
  minor += allThresholdsBonus;
  major += allThresholdsBonus;
  severe += allThresholdsBonus;
  severe += getPassiveBonus(character, 'severe-threshold');

  // Warden of the Elements (Druid) - Earth channel: +Proficiency to all thresholds
  // Note: Complex condition (requires channeling state), kept inline
  if (hasSubclass(character, 'warden-of-the-elements') &&
      character?.isChanneling && character?.channelingElement === 'earth') {
    const prof = character?.proficiency || 2;
    minor += prof; major += prof; severe += prof;
  }

  // Fortified Armor (BLADE): +2 to all thresholds while wearing armor
  if (armorInfo && hasLoadoutCard(character, 'fortified-armor')) {
    minor += 2;
    major += 2;
    severe += 2;
  }

  // Vitality (BLADE L5) permanent choice bonus - +2 to all thresholds if chosen
  // Note: Unique player choice, kept inline
  if (character?.permanentCardChoices?.vitality?.choices?.includes('maxThreshold')) {
    minor += 2;
    major += 2;
    severe += 2;
  }

  // Transcendence (Elemental Origin Sorcerer) - +4 Severe threshold if selected
  // SRD: "Choose two benefits: +4 bonus to your Severe threshold" (among other options)
  if (character?.transcendenceBenefits?.includes('severe')) {
    severe += 4;
  }

  return { minor, major, severe };
};

/**
 * Calculate proficiency: base proficiency + class bonuses
 * @param {object} character - Character data
 * @param {object} classInfo - Class data with features
 * @returns {number} Calculated proficiency value
 */
export const calculateProficiency = (character, classInfo) => {
  let baseProficiency = character.proficiency || 1;

  // Add class-specific proficiency bonuses
  if (classInfo && classInfo.classFeatures) {
    classInfo.classFeatures.forEach(feature => {
      const featureText = (feature.text || '').toLowerCase();
      // Look for proficiency bonuses in class features
      if (featureText.includes('proficiency') && featureText.includes('+1')) {
        baseProficiency += 1;
      }
    });
  }

  // Transcendence (Elemental Origin Sorcerer) - +1 Proficiency if selected
  // SRD: "Choose two benefits: +1 bonus to your Proficiency" (among other options)
  if (character?.transcendenceBenefits?.includes('proficiency')) {
    baseProficiency += 1;
  }

  return baseProficiency;
};

/**
 * Calculate max stress with subclass bonuses
 * @param {number} baseMaxStress - Base max stress from character
 * @param {object} character - Character data (for subclass bonuses)
 * @returns {number} Calculated max stress value
 */
export const calculateMaxStress = (baseMaxStress, character = {}) => {
  let maxStress = baseMaxStress || 5;

  // Config-driven max stress bonuses (ancestry, subclass)
  // Includes: Human High Stamina, Vengeance At Ease
  maxStress += getPassiveBonus(character, 'maxStress');

  // Vitality (BLADE L5) permanent choice bonus - +1 Stress slot if chosen
  // Note: Unique player choice, kept inline
  if (character?.permanentCardChoices?.vitality?.choices?.includes('maxStress')) {
    maxStress += 1;
  }

  return maxStress;
};

/**
 * Calculate max HP with subclass bonuses
 * @param {number} baseMaxHP - Base max HP from character
 * @param {object} character - Character data (for subclass bonuses)
 * @returns {number} Calculated max HP value
 */
export const calculateMaxHP = (baseMaxHP, character = {}) => {
  let maxHP = baseMaxHP || 0;

  // Config-driven max HP bonuses (ancestry, subclass)
  // Includes: Giant Endurance, School of War Battlemage
  maxHP += getPassiveBonus(character, 'maxHp');

  // Vitality (BLADE L5) permanent choice bonus - +1 HP slot if chosen
  // Note: Unique player choice, kept inline
  if (character?.permanentCardChoices?.vitality?.choices?.includes('maxHP')) {
    maxHP += 1;
  }

  return maxHP;
};

/**
 * Get bonus modifier for an Experience from ancestry features
 * @param {object} character - Character data
 * @param {number} experienceIndex - Index of the experience in the experiences array
 * @returns {number} Bonus modifier (0 or +1 for Purposeful Design)
 */
export const getExperienceBonus = (character, experienceIndex) => {
  let bonus = 0;

  // Clank "Purposeful Design" - +1 to chosen Experience
  // SRD: "At character creation, choose one of your Experiences that best aligns
  // with this purpose and gain a permanent +1 bonus to it."
  const ancestry = character?.ancestry;
  const hasClankAncestry = Array.isArray(ancestry)
    ? ancestry.some(a => a?.toLowerCase() === 'clank')
    : ancestry?.toLowerCase() === 'clank';

  if (hasClankAncestry && character?.purposefulDesignExperience === experienceIndex) {
    bonus += 1;
  }

  // Master of the Craft (GRACE L9) permanent choice bonus
  // SRD: "+2 to two Experiences OR +3 to one Experience. Then vault."
  const motc = character?.permanentCardChoices?.['master-of-the-craft'];
  if (motc?.chosen) {
    const selectedIndices = motc.selected || [];
    if (selectedIndices.includes(experienceIndex)) {
      // If mode is 'oneExperience', bonus is +3; otherwise +2 (two experiences)
      bonus += motc.mode === 'oneExperience' ? 3 : 2;
    }
  }

  return bonus;
};

// ============================================================================
// PERMANENT CARD CHOICES (Vault Cards)
// ============================================================================

/**
 * Vault card configurations - cards that grant permanent bonuses then go to vault
 */
const VAULT_CARD_KEYS = ['vitality', 'master-of-the-craft', 'unbreakable'];

/**
 * Validate if a permanent card can be added to loadout
 * Blocks re-addition after permanent choice has been made
 * @param {object} character - Character data with permanentCardChoices
 * @param {string} cardKey - Card key to validate (raw or normalized)
 * @returns {string|null} Error message if blocked, null if allowed
 */
export const validatePermanentCardAddition = (character, cardKey) => {
  const normalizedKey = cardKey.replace('core_domain_card_', '').replace(/_/g, '-');

  // Only vault cards need validation
  if (!VAULT_CARD_KEYS.includes(normalizedKey)) {
    return null; // Not a vault card, allow
  }

  // Check if permanent choice has already been made
  if (character?.permanentCardChoices?.[normalizedKey]?.chosen) {
    return `This card's benefits are already applied permanently.`;
  }

  return null; // Allowed
};

/**
 * Get vitality bonuses from permanent card choices
 * Vitality: Choose 2 of: +1 Stress slot, +1 HP slot, or +2 thresholds
 * @param {object} character - Character data
 * @returns {object} { stressSlots: number, hpSlots: number, thresholdBonus: number }
 */
export const getVitalityBonuses = (character) => {
  const choices = character?.permanentCardChoices?.vitality?.choices || [];
  return {
    stressSlots: choices.includes('maxStress') ? 1 : 0,
    hpSlots: choices.includes('maxHP') ? 1 : 0,
    thresholdBonus: choices.includes('maxThreshold') ? 2 : 0,
  };
};

/**
 * Get master-of-the-craft experience bonuses from permanent card choices
 * Master of the Craft: +2 to two Experiences OR +3 to one Experience
 * @param {object} character - Character data
 * @param {number} experienceIndex - Index of experience to check
 * @returns {number} Bonus modifier for that experience
 */
export const getMasterOfTheCraftBonus = (character, experienceIndex) => {
  const motc = character?.permanentCardChoices?.['master-of-the-craft'];
  if (!motc?.chosen) return 0;

  const selectedIndices = motc.selected || [];
  if (!selectedIndices.includes(experienceIndex)) return 0;

  // If mode is 'oneExperience', bonus is +3; otherwise +2
  return motc.mode === 'oneExperience' ? 3 : 2;
};

/**
 * Check if unbreakable has been used (triggered and vaulted)
 * @param {object} character - Character data
 * @returns {boolean} True if already used
 */
export const isUnbreakableUsed = (character) => {
  return character?.permanentCardChoices?.unbreakable?.chosen === true;
};

/**
 * Helper function to remove undefined values from an object (for Firebase compatibility)
 * @param {*} obj - Object to clean
 * @returns {*} Cleaned object without undefined values
 */
export const cleanObjectForFirebase = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(cleanObjectForFirebase);

  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = cleanObjectForFirebase(value);
    }
  }
  return cleaned;
};
